import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  clientApi,
  GenerateLabelResponse,
  type Annotation,
  type FeedbackSeverity,
  type LabelLayoutMap,
} from '../../api/client';
import { LabelPreviewAnnotator } from '../../components/labels/LabelPreviewAnnotator';
import { useClientTemplates } from '../../hooks/useClientData';
import { useAuthStore } from '../../store/authStore';
import { useImpersonationStore } from '../../store/impersonationStore';

export function TestLabelPage() {
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [appliedTemplateKey, setAppliedTemplateKey] = useState<string | null>(null);
  const [jsonPayload, setJsonPayload] = useState<string>(JSON.stringify({
    poNumber: 'PO123456',
    upc: '012345678905',
    vendorPartNumber: 'VPN-12345',
    cartonQty: '10',
    sscc: '00123456789012345678',
    shipFrom: {
      name: 'Warehouse A',
      address1: '123 Main St',
      city: 'Austin',
      state: 'TX',
      postalCode: '78701',
    },
    shipTo: {
      name: 'Store #100',
      address1: '456 Oak Ave',
      city: 'Houston',
      state: 'TX',
      postalCode: '77001',
    },
    cartonNumber: 1,
    cartonTotal: 5,
  }, null, 2));
  const [result, setResult] = useState<GenerateLabelResponse | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Appearance feedback state
  const [layoutMap, setLayoutMap] = useState<LabelLayoutMap | null>(null);
  const [layoutMapError, setLayoutMapError] = useState<string | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [showOverlay, setShowOverlay] = useState(true);
  const [overallComment, setOverallComment] = useState('');
  const [severity, setSeverity] = useState<FeedbackSeverity | ''>('');
  const [feedbackSuccess, setFeedbackSuccess] = useState<string | null>(null);
  /** Staff-only private GitHub issue URL from submit response (never for API-key clients). */
  const [feedbackGithubUrl, setFeedbackGithubUrl] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const { data: templates } = useClientTemplates();
  const { authMode, isStaff } = useAuthStore();
  const { selectedTenantId } = useImpersonationStore();
  // When staff is impersonating a tenant, pass the tenant ID so the backend
  // can authenticate via SWA client principal instead of API key
  const impersonatedTenantId = authMode === 'aad' && selectedTenantId ? selectedTenantId : undefined;

  const allTemplates = templates || [];
  const requestedTemplate = searchParams.get('template');
  const industries = Array.from(
    new Set(allTemplates.map((t) => t.industry || 'unknown'))
  ).sort();

  const companies = Array.from(
    new Set(
      allTemplates
        .filter((t) => (selectedIndustry ? t.industry === selectedIndustry : true))
        .map((t) => t.company || t.retailer)
    )
  ).sort();

  const filteredTemplates = allTemplates.filter((t) => {
    if (selectedIndustry && t.industry !== selectedIndustry) return false;
    if (selectedCompany && (t.company || t.retailer) !== selectedCompany) return false;
    return true;
  });

  const labelOptions = filteredTemplates.map((t) => ({
    value: t.templateKey,
    label: `${t.templateName || t.templateKey} (${t.labelType})`,
  }));

  useEffect(() => {
    if (!requestedTemplate || requestedTemplate === appliedTemplateKey || allTemplates.length === 0) {
      return;
    }

    setAppliedTemplateKey(requestedTemplate);

    const template = allTemplates.find((item) => item.templateKey === requestedTemplate);
    if (!template) {
      return;
    }

    setSelectedIndustry(template.industry || '');
    setSelectedCompany(template.company || template.retailer || '');
    setSelectedTemplate(template.templateKey);

    if (template.examplePayload?.data) {
      setJsonPayload(JSON.stringify(template.examplePayload.data, null, 2));
    }
  }, [requestedTemplate, appliedTemplateKey, allTemplates]);

  // Fetch layout map after a successful preview for the selected template
  useEffect(() => {
    if (!previewUrl || !selectedTemplate) {
      return;
    }

    let cancelled = false;
    setLayoutMap(null);
    setLayoutMapError(null);

    clientApi
      .getLabelLayoutMap(selectedTemplate, impersonatedTenantId)
      .then((map) => {
        if (!cancelled) {
          setLayoutMap(map);
          setLayoutMapError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLayoutMap(null);
          setLayoutMapError(
            err instanceof Error ? err.message : 'Layout map unavailable',
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [previewUrl, selectedTemplate, impersonatedTenantId]);

  const generateMutation = useMutation({
    mutationFn: (data: { templateKey?: string; data: Record<string, unknown> }) =>
      clientApi.generateLabel({
        templateKey: data.templateKey,
        data: data.data,
      }, impersonatedTenantId),
    onSuccess: (data) => {
      setResult(data);
      setError(null);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Generation failed');
      setResult(null);
    },
  });

  const previewMutation = useMutation({
    mutationFn: async (data: { templateKey?: string; data: Record<string, unknown> }) => {
      const blob = await clientApi.generatePreview({
        templateKey: data.templateKey,
        data: data.data,
        format: 'png',
      }, impersonatedTenantId);
      return URL.createObjectURL(blob);
    },
    onSuccess: (url) => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(url);
      setError(null);
      setAnnotations([]);
      setOverallComment('');
      setSeverity('');
      setFeedbackSuccess(null);
      setFeedbackGithubUrl(null);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Preview failed');
    },
  });

  const feedbackMutation = useMutation({
    mutationFn: async () => {
      let payloadSample: Record<string, unknown> | undefined;
      try {
        payloadSample = JSON.parse(jsonPayload) as Record<string, unknown>;
      } catch {
        payloadSample = undefined;
      }

      const selected = allTemplates.find((t) => t.templateKey === selectedTemplate);

      return clientApi.submitLabelFeedback(
        {
          templateKey: selectedTemplate,
          templateVersion: result?.templateVersion || selected?.version,
          source: 'portal',
          overallComment: overallComment.trim() || undefined,
          severity: severity || undefined,
          payloadSample,
          annotations: annotations.map(
            ({ id: _id, category, targetType, ...rest }) => ({
              ...rest,
              targetType,
              issueCategory: category,
            }),
          ),
        },
        impersonatedTenantId,
      );
    },
    onSuccess: (data) => {
      // Never show private GitHub URLs as plain text for clients.
      // Staff (AAD) may get githubIssueUrl and see a separate link in the UI.
      if (data.escalated) {
        setFeedbackSuccess(
          `Feedback submitted successfully. Reference: ${data.feedbackId}. Our team has been notified.`,
        );
      } else {
        setFeedbackSuccess(
          `Feedback saved successfully. Reference: ${data.feedbackId}.`,
        );
      }
      setFeedbackGithubUrl(data.githubIssueUrl || null);
      setAnnotations([]);
      setOverallComment('');
      setSeverity('');
      setError(null);
    },
    onError: (err) => {
      setFeedbackSuccess(null);
      setFeedbackGithubUrl(null);
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    },
  });

  const handleGenerate = () => {
    try {
      const data = JSON.parse(jsonPayload);
      generateMutation.mutate({
        templateKey: selectedTemplate || undefined,
        data,
      });
    } catch {
      setError('Invalid JSON payload');
    }
  };

  const handlePreview = () => {
    try {
      const data = JSON.parse(jsonPayload);
      previewMutation.mutate({
        templateKey: selectedTemplate || undefined,
        data,
      });
    } catch {
      setError('Invalid JSON payload');
    }
  };

  const canSubmitFeedback =
    Boolean(overallComment.trim()) || annotations.length > 0;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Test Label Generation</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* Input Panel */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Input</h2>
          
          <div className="space-y-4">
            <div>
              <label className="label">Industry</label>
              <select
                className="input"
                value={selectedIndustry}
                onChange={(e) => {
                  setSelectedIndustry(e.target.value);
                  setSelectedCompany('');
                  setSelectedTemplate('');
                }}
              >
                <option value="">Select industry...</option>
                {industries.map((industry) => (
                  <option key={industry} value={industry}>
                    {industry}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">Company</label>
              <select
                className="input"
                value={selectedCompany}
                onChange={(e) => {
                  setSelectedCompany(e.target.value);
                  setSelectedTemplate('');
                }}
                disabled={!selectedIndustry}
              >
                <option value="">Select company...</option>
                {companies.map((company) => (
                  <option key={company} value={company}>
                    {company}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">Label Template</label>
              <select
                className="input"
                value={selectedTemplate}
                onChange={(e) => {
                  const key = e.target.value;
                  setSelectedTemplate(key);

                  // Auto-populate with example payload if available
                  if (key) {
                    const template = allTemplates.find(t => t.templateKey === key);
                    if (template?.examplePayload?.data) {
                      setJsonPayload(JSON.stringify(template.examplePayload.data, null, 2));
                    }
                  }
                }}
                disabled={!selectedCompany}
              >
                <option value="">Select label...</option>
                {labelOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {(selectedIndustry || selectedCompany || selectedTemplate) && (
              <button
                type="button"
                className="btn btn-secondary w-full text-sm"
                onClick={() => {
                  setSelectedIndustry('');
                  setSelectedCompany('');
                  setSelectedTemplate('');
                }}
              >
                Clear selection
              </button>
            )}

            <div>
              <label className="label">JSON Payload</label>
              <textarea
                className="input font-mono text-sm"
                rows={15}
                value={jsonPayload}
                onChange={(e) => setJsonPayload(e.target.value)}
                placeholder="Enter JSON data..."
              />
            </div>

            {/* Field checklist for the selected template */}
            {selectedTemplate && (() => {
              const tmpl = allTemplates.find(t => t.templateKey === selectedTemplate);
              if (!tmpl?.fields || tmpl.fields.length === 0) return null;
              const normalizedFields = tmpl.fields.map((f: unknown) => {
                if (typeof f === 'string') {
                  return { name: f, type: 'string' as const, required: true };
                }
                const obj = f as { name: string; type?: string; required?: boolean };
                return { name: obj.name, type: obj.type || 'string', required: obj.required !== false };
              });
              const required = normalizedFields.filter(f => f.required);
              const optional = normalizedFields.filter(f => !f.required);
              return (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-blue-800 mb-2">Template Fields Reference</p>
                  {required.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs font-medium text-blue-700 mb-1">Required</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                        {required.map(f => (
                          <span key={f.name} className="text-xs text-blue-900">
                            <span className="text-red-500 mr-1">●</span>
                            <code className="bg-blue-100 px-1 rounded">{f.name}</code>
                            <span className="text-blue-500 ml-1">({f.type})</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {optional.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-blue-700 mb-1">Optional</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                        {optional.map(f => (
                          <span key={f.name} className="text-xs text-blue-900">
                            <span className="text-gray-400 mr-1">○</span>
                            <code className="bg-blue-100 px-1 rounded">{f.name}</code>
                            <span className="text-blue-500 ml-1">({f.type})</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="flex space-x-3">
              <button
                className="btn btn-primary flex-1"
                onClick={handleGenerate}
                disabled={generateMutation.isPending || !selectedTemplate}
              >
                {generateMutation.isPending ? 'Generating...' : 'Generate ZPL'}
              </button>
              <button
                className="btn btn-secondary flex-1"
                onClick={handlePreview}
                disabled={previewMutation.isPending || !selectedTemplate}
              >
                {previewMutation.isPending ? 'Loading...' : 'Preview'}
              </button>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Output Panel */}
        <div className="space-y-6">
          {/* ZPL Output */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">ZPL Output</h2>
            {result ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">
                    Template: {result.templateKey} v{result.templateVersion}
                  </span>
                  <button
                    className="text-primary-600 hover:text-primary-800"
                    onClick={() => navigator.clipboard.writeText(result.zpl)}
                  >
                    Copy
                  </button>
                </div>
                <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-x-auto max-h-64">
                  {result.zpl}
                </pre>
                {result.warnings.length > 0 && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="font-medium text-yellow-800 text-sm">Warnings:</p>
                    <ul className="list-disc list-inside text-yellow-700 text-sm">
                      {result.warnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                Click "Generate ZPL" to see output
              </p>
            )}
          </div>

          {/* Preview + Appearance Feedback */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Preview</h2>
            {previewUrl ? (
              <div className="space-y-4">
                <LabelPreviewAnnotator
                  previewUrl={previewUrl}
                  layoutMap={layoutMap}
                  annotations={annotations}
                  onChangeAnnotations={setAnnotations}
                  showOverlay={showOverlay}
                  onToggleOverlay={setShowOverlay}
                  onAnnotationCommitted={(ann, zoneLabel) => {
                    const target = ann.fieldName
                      ? `Field ${ann.fieldName}`
                      : ann.zoneId
                        ? `Zone ${ann.zoneId}${zoneLabel ? ` (${zoneLabel})` : ''}`
                        : zoneLabel || 'Region';
                    const line = `${target}: ${ann.comment}`;
                    setOverallComment((prev) => {
                      const p = prev.trim();
                      if (!p) return line;
                      if (p.includes(line)) return p;
                      return `${p}\n${line}`;
                    });
                  }}
                />

                {layoutMapError && (
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    Layout overlay unavailable ({layoutMapError}). You can still
                    pin regions and submit free-text feedback.
                  </p>
                )}

                <div className="border-t border-gray-200 pt-4 space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Appearance Feedback
                  </h3>

                  <div>
                    <label className="label">Overall comment</label>
                    <textarea
                      className="input text-sm"
                      rows={3}
                      value={overallComment}
                      onChange={(e) => {
                        setOverallComment(e.target.value);
                        setFeedbackSuccess(null);
                        setFeedbackGithubUrl(null);
                      }}
                      placeholder="Describe overall appearance issues or notes..."
                    />
                  </div>

                  <div>
                    <label className="label">Severity (optional)</label>
                    <select
                      className="input text-sm"
                      value={severity}
                      onChange={(e) =>
                        setSeverity(e.target.value as FeedbackSeverity | '')
                      }
                    >
                      <option value="">None (save only)</option>
                      <option value="low">Low (save only)</option>
                      <option value="medium">Medium — notify our team</option>
                      <option value="high">High — notify our team</option>
                      <option value="critical">Critical — notify our team</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Feedback is always stored. Our team is notified for{' '}
                      <strong>Medium / High / Critical</strong>, or when a pin uses
                      category barcode / missing / wrong content.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="btn btn-primary w-full text-sm"
                    disabled={
                      !canSubmitFeedback ||
                      feedbackMutation.isPending ||
                      !selectedTemplate
                    }
                    onClick={() => feedbackMutation.mutate()}
                  >
                    {feedbackMutation.isPending
                      ? 'Submitting...'
                      : 'Submit Appearance Feedback'}
                  </button>

                  {feedbackSuccess && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm space-y-1">
                      <p>{feedbackSuccess}</p>
                      {isStaff && feedbackGithubUrl && (
                        <p>
                          <a
                            href={feedbackGithubUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-primary-700 underline hover:text-primary-900"
                          >
                            View GitHub issue
                          </a>
                          <span className="text-green-700/80 text-xs ml-1">
                            (staff only · private repo)
                          </span>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                Click "Preview" to render the label
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
